import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Briefcase, Plus, Search, ArrowRight, Trophy, XCircle, Pause,
  Clock, IndianRupee, User, Building2, Filter
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import Layout from "../components/Layout";
import { API } from "../App";
import { toast } from "sonner";

const formatINR = (v) => {
  if (!v) return "0";
  const n = Number(v);
  if (n >= 10000000) return `${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `${(n / 100000).toFixed(1)} L`;
  return n.toLocaleString("en-IN");
};

const STATUS_STYLES = {
  active: "bg-blue-100 text-blue-700",
  won: "bg-emerald-100 text-emerald-700",
  lost: "bg-red-100 text-red-700",
  stalled: "bg-amber-100 text-amber-700",
};

const STATUS_ICONS = {
  active: Clock,
  won: Trophy,
  lost: XCircle,
  stalled: Pause,
};

const DealRoomsListPage = () => {
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [leads, setLeads] = useState([]);
  const [selectedLeadId, setSelectedLeadId] = useState("");
  const [dealValue, setDealValue] = useState("");
  const [creating, setCreating] = useState(false);

  const fetchDeals = async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus !== "all") params.append("status", filterStatus);
      const res = await axios.get(`${API}/deal-rooms?${params.toString()}`);
      setDeals(res.data);
    } catch { toast.error("Failed to load deal rooms"); }
    setLoading(false);
  };

  const fetchLeads = async () => {
    try {
      const res = await axios.get(`${API}/leads?limit=100`);
      setLeads(Array.isArray(res.data) ? res.data : res.data.leads || []);
    } catch {}
  };

  useEffect(() => { fetchDeals(); }, [filterStatus]);

  const handleCreate = async () => {
    if (!selectedLeadId) { toast.error("Select a lead"); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/deal-rooms`, {
        lead_id: selectedLeadId,
        deal_value: Number(dealValue) || 0,
      });
      toast.success("Deal Room created");
      setCreateOpen(false);
      setSelectedLeadId("");
      setDealValue("");
      navigate(`/deal-rooms/${res.data.deal_room_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create");
    }
    setCreating(false);
  };

  const filteredDeals = deals.filter(d => {
    if (!search) return true;
    const s = search.toLowerCase();
    return d.lead_name?.toLowerCase().includes(s) 
      || d.project_name?.toLowerCase().includes(s)
      || d.lead_phone?.toLowerCase().includes(s)
      || d.lead_email?.toLowerCase().includes(s)
      || d.lead_id?.toLowerCase().includes(s);
  });

  const stats = {
    total: deals.length,
    active: deals.filter(d => d.status === "active").length,
    won: deals.filter(d => d.status === "won").length,
    totalValue: deals.reduce((s, d) => s + (d.deal_value || 0), 0),
  };

  return (
    <Layout>
      <div className="p-6 space-y-6 max-w-7xl mx-auto" data-testid="deal-rooms-list">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Deal Rooms</h1>
            <p className="text-sm text-slate-500 mt-1">{stats.total} deals &middot; ₹{formatINR(stats.totalValue)} pipeline</p>
          </div>
          <p className="text-xs text-slate-400 bg-slate-50 px-3 py-1.5 rounded-md border border-slate-200">
            Deal Rooms are auto-created when a lead moves to Negotiation stage
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-white border-slate-200"><CardContent className="p-4">
            <p className="text-xs text-slate-500">Total Deals</p>
            <p className="text-2xl font-bold text-slate-900 font-['Manrope']">{stats.total}</p>
          </CardContent></Card>
          <Card className="bg-white border-slate-200"><CardContent className="p-4">
            <p className="text-xs text-slate-500">Active</p>
            <p className="text-2xl font-bold text-blue-600 font-['Manrope']">{stats.active}</p>
          </CardContent></Card>
          <Card className="bg-white border-slate-200"><CardContent className="p-4">
            <p className="text-xs text-slate-500">Won</p>
            <p className="text-2xl font-bold text-emerald-600 font-['Manrope']">{stats.won}</p>
          </CardContent></Card>
          <Card className="bg-white border-slate-200"><CardContent className="p-4">
            <p className="text-xs text-slate-500">Pipeline Value</p>
            <p className="text-2xl font-bold text-slate-900 font-['Manrope']">₹{formatINR(stats.totalValue)}</p>
          </CardContent></Card>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search deals..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9" data-testid="deal-search" />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-32 h-9" data-testid="deal-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="won">Won</SelectItem>
              <SelectItem value="lost">Lost</SelectItem>
              <SelectItem value="stalled">Stalled</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Deals Grid */}
        {loading ? (
          <div className="text-center py-12 text-slate-500">Loading deal rooms...</div>
        ) : filteredDeals.length === 0 ? (
          <div className="text-center py-16">
            <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No deal rooms yet</p>
            <p className="text-sm text-slate-400 mt-1">Create one from a lead to get started</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredDeals.map(deal => {
              const StatusIcon = STATUS_ICONS[deal.status] || Clock;
              const milestonesCompleted = (deal.milestones || []).filter(m => m.status === "completed").length;
              const milestonesTotal = (deal.milestones || []).length;
              return (
                <Card
                  key={deal.deal_room_id}
                  className="bg-white border-slate-200 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/deal-rooms/${deal.deal_room_id}`)}
                  data-testid={`deal-card-${deal.deal_room_id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-slate-900 font-['Manrope']">{deal.lead_name}</h3>
                        <p className="text-sm text-slate-500">{deal.project_name || "No project"}</p>
                      </div>
                      <Badge className={`${STATUS_STYLES[deal.status]} capitalize text-xs`}>
                        <StatusIcon className="w-3 h-3 mr-1" /> {deal.status}
                      </Badge>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500 flex items-center gap-1"><IndianRupee className="w-3.5 h-3.5" /> Deal Value</span>
                        <span className="font-semibold text-slate-800">₹{formatINR(deal.deal_value)}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500 flex items-center gap-1"><User className="w-3.5 h-3.5" /> Assigned</span>
                        <span className="text-slate-700">{deal.assigned_to_name || "-"}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">Milestones</span>
                        <span className="text-slate-700">{milestonesCompleted}/{milestonesTotal}</span>
                      </div>
                      {/* Milestone progress bar */}
                      <div className="w-full bg-slate-100 rounded-full h-1.5 mt-1">
                        <div
                          className="bg-emerald-500 h-1.5 rounded-full transition-all"
                          style={{ width: milestonesTotal > 0 ? `${(milestonesCompleted / milestonesTotal) * 100}%` : "0%" }}
                        />
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                      <span className="text-xs text-slate-400">{new Date(deal.created_at).toLocaleDateString()}</span>
                      <span className="text-xs text-blue-600 font-medium flex items-center gap-1">
                        Open <ArrowRight className="w-3 h-3" />
                      </span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Create Dialog */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="sm:max-w-md" data-testid="create-deal-dialog">
            <DialogHeader>
              <DialogTitle className="font-['Manrope']">Create Deal Room</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Select Lead</Label>
                <Select value={selectedLeadId} onValueChange={setSelectedLeadId}>
                  <SelectTrigger data-testid="select-lead-for-deal"><SelectValue placeholder="Choose a lead..." /></SelectTrigger>
                  <SelectContent>
                    {leads.map(l => (
                      <SelectItem key={l.lead_id} value={l.lead_id}>{l.name} {l.project_interest ? `— ${l.project_interest}` : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Deal Value (₹)</Label>
                <Input type="number" placeholder="e.g. 8500000" value={dealValue} onChange={e => setDealValue(e.target.value)} data-testid="deal-value-input" />
              </div>
              <Button onClick={handleCreate} disabled={creating} className="w-full" data-testid="confirm-create-deal-btn">
                {creating ? "Creating..." : "Create Deal Room"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default DealRoomsListPage;
